import Typography from '@components/v2/ui/Typography/Typography';
import { Button } from '@mui/material';
import useWidth from '../../../../components/Hooks/useWidth';
import LangEnum from '../../../../lang/LangEnum';
import useTeceoStyles from '../../../../useTeceoStyles';
import { formatDate, userTimeZone } from '../../../../utils/dates';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { useSignatureContext } from '../contexts/signature.context';
import { Signature, SignedContract } from '../signed-contract';
import { ModalContractPage } from '../types';

type ContractCellActionsProps = {
  signature: Signature | null;
  contract: SignedContract;
  signingAs: 'BRAND' | 'STORE';
  fullWidth?: boolean;
  breakLine?: boolean;
};

const userSigned = (signedAt: Date | string | null): string =>
  signedAt ? `${formatDate(signedAt, 'P p', userTimeZone)}` : '';

const truncate = (str: string, n: number) => (str.length > n ? `${str.substr(0, n - 1)}...` : str);

const ContractCellActions = ({
  signature,
  signingAs,
  contract,
  fullWidth,
  breakLine = true,
}: ContractCellActionsProps) => {
  const { translate } = useTranslation();
  const { changePage, changeSigningAs, changeContractInfo, isReadOnly } = useSignatureContext();
  const { isStore } = useApplicationContext();
  const { smallWidth } = useWidth();
  const { classes: teceoClasses } = useTeceoStyles();
  const additionalInfo = contract.signatureAdditionalInfo?.find(
    (info) => info.signAs === signingAs,
  );
  const getSignatureName = () => {
    if (additionalInfo) {
      return additionalInfo.name;
    }

    if (signature) {
      return signature.name;
    }

    return '';
  };

  const truncatedName = truncate(getSignatureName(), smallWidth ? 20 : 14);

  const nameAndCreatedAt = `${getSignatureName()} (${userSigned(
    signature?.signedAt || contract.userBrandSignedAt || contract.userStoreSignedAt,
  )})`;

  const nameAndCreateAtTruncated = `${truncatedName} (${userSigned(
    signature?.signedAt || contract.userBrandSignedAt || contract.userStoreSignedAt,
  )})`;

  const getIsSigned = () => {
    if (signature) {
      return true;
    }

    if (signingAs === 'BRAND') {
      return !!contract.userBrandSignedAt;
    }

    if (signingAs === 'STORE') {
      return !!contract.userStoreSignedAt;
    }

    return false;
  };

  const handleAddSignature = () => {
    changeSigningAs(signingAs);
    changeContractInfo({
      version: contract.version,
      createdAt: contract.createdAt,
      id: contract.id,
    });
    changePage(ModalContractPage.METHOD_OF_SIGNING, contract);
  };

  if (isStore && !getIsSigned() && signingAs === 'BRAND') {
    return null;
  }

  if (!getIsSigned() && isReadOnly) {
    return <Typography color="text.secondary">{translate(LangEnum.GLOBAL_NOT_SIGNED)}</Typography>;
  }

  if (!getIsSigned()) {
    return (
      <Button
        fullWidth={fullWidth}
        variant="contained"
        color="primary"
        onClick={handleAddSignature}
      >
        <Typography>
          {translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_ACTION_ADD_SIGN)}
        </Typography>
      </Button>
    );
  }

  if (breakLine) {
    return (
      <>
        <Typography title={getSignatureName()} className={teceoClasses.truncate}>
          {getSignatureName()}
        </Typography>
        <Typography variant="caption">
          {signature
            ? userSigned(signature.signedAt)
            : userSigned(
                signingAs === 'BRAND' ? contract.userBrandSignedAt : contract.userStoreSignedAt,
              )}
        </Typography>
      </>
    );
  }

  return <Typography title={nameAndCreatedAt}>{nameAndCreateAtTruncated}</Typography>;
};

export default ContractCellActions;
